/** Dashboard stats — one outcome per test (report), not per metric */

const parseFormattedDate = (dateStr) => {
  if (!dateStr) return null;
  const parts = dateStr.split('.');
  if (parts.length !== 3) return null;
  const [day, month, year] = parts.map(Number);
  if (!day || !month || !year) return null;
  return new Date(year, month - 1, day);
};

export const classifyTestResultStatus = (report) => {
  const statuses = (report.analysis || [])
    .map((a) => a.resultStatus)
    .filter(Boolean);

  if (statuses.includes('Kazandı')) return 'Kazandı';
  if (statuses.includes('Kaybetti')) return 'Kaybetti';
  if (statuses.includes('Etkisiz')) return 'Etkisiz';
  return null;
};

export const aggregateResultStatusByTest = (reports) => {
  const counts = { kazandi: 0, kaybetti: 0, etkisiz: 0 };
  let testsWithResult = 0;

  reports.forEach((report) => {
    const status = classifyTestResultStatus(report);
    if (!status) return;
    testsWithResult += 1;
    if (status === 'Kazandı') counts.kazandi += 1;
    else if (status === 'Kaybetti') counts.kaybetti += 1;
    else counts.etkisiz += 1;
  });

  return { counts, testsWithResult };
};

export const getWinningTestUplift = (report) => {
  const improvements = (report.analysis || [])
    .filter((a) => a.resultStatus === 'Kazandı')
    .map((a) => a.improvement)
    .filter((v) => typeof v === 'number' && !Number.isNaN(v));
  return improvements.length ? Math.max(...improvements) : null;
};

export const aggregateDecisionDistribution = (reports) => {
  const counts = { apply: 0, reject: 0, retest: 0, pending: 0 };

  reports.forEach((report) => {
    const key = report.decision || 'pending';
    if (Object.prototype.hasOwnProperty.call(counts, key)) counts[key] += 1;
    else counts.pending += 1;
  });

  return [
    { id: 'Uygula', label: 'Uygula', value: counts.apply },
    { id: 'Reddet', label: 'Reddet', value: counts.reject },
    { id: 'Tekrar Test', label: 'Tekrar Test', value: counts.retest },
    { id: 'Beklemede', label: 'Beklemede', value: counts.pending },
  ];
};

export const countSignificantTests = (reports) =>
  reports.filter((report) =>
    (report.analysis || []).some((a) => a.stats?.isSignificant === true),
  ).length;

// Recent activity list for statistics page
export const aggregateTaxonomyWinRates = (reports, field, typeOptions) => {
  const map = new Map();

  reports.forEach((report) => {
    const key = report[field];
    if (!key) return;

    const outcome = classifyTestResultStatus(report);
    if (!map.has(key)) {
      map.set(key, { key, total: 0, won: 0, lost: 0, inconclusive: 0 });
    }
    const bucket = map.get(key);
    bucket.total += 1;
    if (outcome === 'Kazandı') bucket.won += 1;
    else if (outcome === 'Kaybetti') bucket.lost += 1;
    else if (outcome === 'Etkisiz') bucket.inconclusive += 1;
  });

  const labelFor = (value) =>
    typeOptions.find((t) => t.value === value)?.label || value;

  return Array.from(map.values())
    .filter((b) => b.total > 0)
    .sort((a, b) => b.total - a.total)
    .slice(0, 8)
    .map((b) => ({
      id: b.key,
      label: labelFor(b.key),
      total: b.total,
      winRate: Math.round((b.won / b.total) * 100),
      Kazandı: b.won,
      Kaybetti: b.lost,
      Etkisiz: b.inconclusive,
    }));
};

const getReportResultDate = (report) =>
  report.status === 'Durduruldu'
    ? parseFormattedDate(report.formattedEndDate) || new Date(report.createdAt)
    : new Date(report.createdAt);

const buildLast6Months = () => {
  const now = new Date();
  const months = [];
  for (let i = 0; i < 6; i++) {
    const month = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.unshift({
      month: month.toLocaleString('tr-TR', { month: 'long' }),
      year: month.getFullYear(),
      index: month.getMonth(),
    });
  }
  return months;
};

export const aggregateMonthlyOutcomes = (reports) =>
  buildLast6Months().map((monthObj) => {
    let kazandi = 0;
    let kaybetti = 0;
    let etkisiz = 0;

    reports.forEach((report) => {
      const outcome = classifyTestResultStatus(report);
      if (!outcome) return;

      const refDate = getReportResultDate(report);
      if (
        refDate.getMonth() === monthObj.index &&
        refDate.getFullYear() === monthObj.year
      ) {
        if (outcome === 'Kazandı') kazandi += 1;
        else if (outcome === 'Kaybetti') kaybetti += 1;
        else etkisiz += 1;
      }
    });

    return {
      month: monthObj.month,
      Kazandı: kazandi,
      Kaybetti: kaybetti,
      Etkisiz: etkisiz,
    };
  });

export const aggregateWinRateTrend = (reports) =>
  buildLast6Months().map((monthObj) => {
    let won = 0;
    let total = 0;

    reports.forEach((report) => {
      const outcome = classifyTestResultStatus(report);
      if (!outcome) return;

      const refDate = getReportResultDate(report);
      if (
        refDate.getMonth() !== monthObj.index ||
        refDate.getFullYear() !== monthObj.year
      ) {
        return;
      }

      total += 1;
      if (outcome === 'Kazandı') won += 1;
    });

    return {
      month: monthObj.month,
      winRate: total > 0 ? Math.round((won / total) * 1000) / 10 : 0,
      testsWithResult: total,
      won,
    };
  });

export const aggregateAnalystLeaderboard = (reports) => {
  const map = new Map();

  reports.forEach((report) => {
    const analystId =
      report.assignedTo?._id?.toString() ||
      report.assignedTo?.toString() ||
      'unassigned';
    const username =
      report.assignedTo?.username ||
      (analystId === 'unassigned' ? 'Atanmamış' : 'Analist');

    if (!map.has(analystId)) {
      map.set(analystId, {
        analystId,
        username,
        testCount: 0,
        testsWithResult: 0,
        wins: 0,
      });
    }

    const row = map.get(analystId);
    row.testCount += 1;

    const outcome = classifyTestResultStatus(report);
    if (!outcome) return;

    row.testsWithResult += 1;
    if (outcome === 'Kazandı') row.wins += 1;
  });

  return Array.from(map.values())
    .map((row) => ({
      ...row,
      winRate:
        row.testsWithResult > 0
          ? Math.round((row.wins / row.testsWithResult) * 1000) / 10
          : 0,
    }))
    .sort((a, b) => b.winRate - a.winRate || b.testsWithResult - a.testsWithResult)
    .slice(0, 10);
};

export const aggregateMetricHeatmap = (reports, limit = 12) => {
  const map = new Map();

  reports.forEach((report) => {
    (report.analysis || []).forEach((entry) => {
      const name = (entry.name || '').trim();
      const status = entry.resultStatus;
      if (!name || !status) return;

      if (!map.has(name)) {
        map.set(name, { metric: name, Kazandı: 0, Kaybetti: 0, Etkisiz: 0, total: 0 });
      }
      const bucket = map.get(name);
      bucket.total += 1;
      if (status === 'Kazandı') bucket.Kazandı += 1;
      else if (status === 'Kaybetti') bucket.Kaybetti += 1;
      else if (status === 'Etkisiz') bucket.Etkisiz += 1;
    });
  });

  return Array.from(map.values())
    .sort((a, b) => b.total - a.total)
    .slice(0, limit)
    .map((row) => ({
      ...row,
      winRate: row.total > 0 ? Math.round((row.Kazandı / row.total) * 100) : 0,
    }));
};

export const computeLifecycleStats = (reports, testVelocity = {}) => {
  const durations = reports
    .map((r) => r.testDuration)
    .filter((d) => typeof d === 'number' && d > 0);

  const avgTestDurationDays = durations.length
    ? Math.round(durations.reduce((sum, d) => sum + d, 0) / durations.length)
    : 0;

  return {
    avgTestDurationDays,
    testsWithDuration: durations.length,
    draftTests: reports.filter((r) => r.status === 'Taslak').length,
    activeTests: reports.filter((r) => r.status === 'Canlı').length,
    stoppedTests: reports.filter((r) => r.status === 'Durduruldu').length,
    testVelocity,
  };
};

export const summarizeRecentReports = (reports, limit = 5) => {
  const sorted = [...reports].sort(
    (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0),
  );

  return sorted.slice(0, limit).map((report) => ({
    id: report._id,
    name: report.reportName,
    status: report.status,
    brand:
      report.brand?.name ||
      (typeof report.brand === 'object' ? report.brand?.name : null) ||
      '—',
    createdAt: report.createdAt,
    resultStatus: classifyTestResultStatus(report),
    decision: report.decision || 'pending',
  }));
};
